// MobileConsolePage.tsx — the phone shell.
//
// Andrew's shape, and it is the right one: "for mobile we drop the rail concept
// and try to model more like slack/imessage. You select the thread and then the
// thread becomes full screen with controls at the top."
//
// So this is a two-screen stack, not a squeezed console:
//
//   nothing selected  ->  the list, full width
//   a thread selected ->  that thread, full screen, with a way back
//
// NO CONTEXT PANE AND NO AI. Deliberate, and Andrew's call: "No AI visible in
// the mobile entirely, mobile will mostly be techs and we don't need it like
// dispatch does." It also means no AI reads fire from a phone, so that cost
// stays tied to dispatch actually using the feature.
//
// Composition only — every pane already reads the store itself, so there is no
// second copy of any logic here. The desktop shell is untouched.
import { Header } from '../components/shell'
import { QueueRail } from '../components/rail'
import { ThreadPane } from '../components/thread'
import { useStore } from '../store'

export function MobileConsolePage() {
  const activeId = useStore((s) => s.activeId)
  const selectThread = useStore((s) => s.selectThread)
  const threads = useStore((s) => s.threads)
  const active = threads.find((t) => t.id === activeId) ?? null

  // A thread that vanished from under us (closed by somebody else) must not
  // leave a blank screen with no way out.
  if (activeId && !active) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-page">
        <Header />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-[13px] text-mid">That conversation is no longer open.</p>
          <button
            type="button"
            data-testid="mobile-back-to-list"
            onClick={() => selectThread(null)}
            className="rounded-[8px] border border-subtle bg-card px-3 py-2 text-[13px] text-hi"
          >
            Back to the queue
          </button>
        </div>
      </div>
    )
  }

  if (!activeId) {
    return (
      <div data-testid="mobile-list-view" className="flex h-full min-h-0 flex-col bg-page">
        <Header />
        {/* The rail sizes itself to 340px for the desktop shell; on a phone it
            is the whole screen. `flex-1` overrides that without the rail
            needing to know which shell it is in. */}
        <div className="flex min-h-0 flex-1 [&>div]:w-full [&>div]:flex-1">
          <QueueRail />
        </div>
      </div>
    )
  }

  return (
    <div data-testid="mobile-thread-view" className="flex h-full min-h-0 flex-col bg-page">
      {/* CONTROLS AT THE TOP, and the back control first — this is the only
          way out of a full-screen thread, so it is the one thing that must
          never be scrolled to or hidden behind a menu. */}
      <div className="flex flex-none items-center gap-2 border-b border-subtle bg-sidebar px-3 py-2">
        <button
          type="button"
          data-testid="mobile-back-to-list"
          aria-label="Back to the queue"
          onClick={() => selectThread(null)}
          className="flex h-8 min-w-8 items-center justify-center rounded-[8px] border border-subtle bg-card px-2 text-[15px] text-hi"
        >
          ‹
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-hi">{active?.who}</div>
          <div className="truncate text-[11px] text-lo">
            {active?.client ?? 'No known company'} · {active?.channel}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <ThreadPane />
      </div>
    </div>
  )
}
